import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowDown, Ban, Check } from "lucide-react";
import {
  GUARDRAILS_GATES,
  GUARDRAILS_HEADLINE,
  GUARDRAILS_INPUT,
  GUARDRAILS_OUTPUT,
  GUARDRAILS_PAYOFF,
  GUARDRAILS_SCAN_PILLS,
  GUARDRAILS_STAMP,
  GUARDRAILS_SUBHEAD,
} from "./guardrailsContent";

const FunnelConnector = () => (
  <div className="flex flex-col items-center py-1" aria-hidden="true">
    <div className="w-px h-3 bg-gradient-to-b from-white/10 to-orange-500/30" />
    <ArrowDown className="w-3.5 h-3.5 text-orange-500/40" />
    <div className="w-px h-3 bg-gradient-to-b from-orange-500/30 to-white/10" />
  </div>
);

const GuardrailsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 overflow-hidden"
      data-testid="inventory-section"
      ref={ref}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 0%, rgba(20,184,166,0.04) 0%, transparent 45%), radial-gradient(circle at 50% 100%, rgba(249,115,22,0.05) 0%, transparent 40%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="badge-mono text-[10px] font-bold tracking-[0.28em] text-teal-400/90 uppercase mb-4">
              {GUARDRAILS_STAMP}
            </p>

            <div
              className="flex flex-wrap gap-2 mb-5"
              data-testid="guardrails-scan-pills"
            >
              {GUARDRAILS_SCAN_PILLS.map((pill) => (
                <div
                  key={pill.label}
                  className="flex items-baseline gap-1.5 px-2.5 py-1.5 rounded-md border border-teal-500/20 bg-teal-500/5"
                >
                  <span className="font-mono font-bold text-base text-teal-400 leading-none">
                    {pill.value}
                  </span>
                  <span className="text-[9px] font-mono text-gray-500 tracking-wide">
                    {pill.label}
                  </span>
                </div>
              ))}
            </div>

            <h2
              className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-4"
              data-testid="guardrails-headline"
            >
              {GUARDRAILS_HEADLINE.lead}{" "}
              <span className="text-orange-400">{GUARDRAILS_HEADLINE.accent}</span>
            </h2>

            <p className="font-body text-sm sm:text-base text-[#9CA3AF] leading-relaxed mb-6">
              {GUARDRAILS_SUBHEAD}
            </p>

            <p
              className="text-sm text-gray-400 font-body leading-relaxed border-l-2 border-teal-500/30 pl-4"
              data-testid="guardrails-payoff"
            >
              {GUARDRAILS_PAYOFF}
            </p>
          </div>

          <div className="lg:col-span-7" data-testid="guardrails-filter-stack">
            <div className="rounded-2xl border border-white/[0.08] bg-[#0a0e14]/80 p-4 sm:p-6">
              <div className="flex items-center justify-center mb-2">
                <span className="badge-mono text-[10px] text-gray-500 tracking-widest px-3 py-1 rounded-full border border-white/[0.06] bg-white/[0.02]">
                  {GUARDRAILS_INPUT}
                </span>
              </div>

              <FunnelConnector />

              <div className="space-y-0">
                {GUARDRAILS_GATES.map((gate, index) => {
                  const isHero = gate.highlight;

                  return (
                    <React.Fragment key={gate.id}>
                      <motion.div
                        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                        animate={
                          reduceMotion || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }
                        }
                        transition={{ duration: 0.35, delay: index * 0.1 }}
                        className={`relative rounded-xl border overflow-hidden ${
                          isHero
                            ? "border-orange-500/35 ring-1 ring-orange-500/20"
                            : "border-white/[0.07]"
                        }`}
                        data-testid={`inventory-card-${index}`}
                      >
                        {isHero && (
                          <span className="absolute top-2 right-2 badge-mono text-[8px] text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full z-10">
                            KEY GUARDRAIL
                          </span>
                        )}

                        <div
                          className={`px-4 py-3 border-b ${
                            isHero
                              ? "border-orange-500/20 bg-orange-500/[0.07]"
                              : "border-white/[0.06] bg-white/[0.02]"
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="badge-mono text-[9px] text-gray-600">{gate.tag}</span>
                            <span
                              className={`font-mono text-xs font-bold tracking-wider ${
                                isHero ? "text-orange-400" : "text-teal-400/80"
                              }`}
                            >
                              {gate.label}
                            </span>
                          </div>
                          <p className="font-heading font-semibold text-sm text-white">
                            {gate.headline}
                          </p>
                        </div>

                        <div className="grid grid-cols-2 divide-x divide-white/[0.06]">
                          <div className="px-3 sm:px-4 py-3 sm:py-3.5 bg-teal-500/[0.04]">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Check className="w-3 h-3 text-teal-400/80" aria-hidden="true" />
                              <span className="badge-mono text-[8px] text-teal-400/70 tracking-wider">
                                ALLOW
                              </span>
                            </div>
                            <p className="text-[10px] sm:text-[11px] text-gray-400 font-body leading-snug">
                              {gate.allow}
                            </p>
                          </div>
                          <div className="px-3 sm:px-4 py-3 sm:py-3.5 bg-red-500/[0.03]">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Ban className="w-3 h-3 text-red-400/60" aria-hidden="true" />
                              <span className="badge-mono text-[8px] text-red-400/50 tracking-wider">
                                BLOCK
                              </span>
                            </div>
                            <p className="text-[10px] sm:text-[11px] text-gray-500 font-body leading-snug line-through decoration-red-500/25">
                              {gate.block}
                            </p>
                          </div>
                        </div>
                      </motion.div>

                      {index < GUARDRAILS_GATES.length - 1 && <FunnelConnector />}
                    </React.Fragment>
                  );
                })}
              </div>

              <FunnelConnector />

              <div className="flex items-center justify-center">
                <span className="badge-mono text-[10px] text-teal-400 tracking-wider px-4 py-2 rounded-full border border-teal-500/25 bg-teal-500/10 font-semibold">
                  {GUARDRAILS_OUTPUT}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuardrailsSection;
